package com.eladfinish.bible.contest.screens.completeVerse

import androidx.compose.runtime.Immutable
import androidx.compose.runtime.Stable
import com.eladfinish.bible.contest.arch.ActionKt
import com.eladfinish.bible.contest.arch.EffectKt
import com.eladfinish.bible.contest.arch.StateKt
import com.eladfinish.bible.contest.common.gender.HebrewGender
import com.eladfinish.bible.contest.data.ChapterData
import com.eladfinish.bible.contest.screens.completeVerse.model.CompleteMode
import com.eladfinish.bible.contest.screens.completeVerse.model.VersePart
import com.eladfinish.bible.contest.screens.completeVerse.model.VerseWithBlanks
import com.eladfinish.bible.contest.screens.search.model.ClickType
import com.eladfinish.bible.contest.screens.search.model.HorizontalDirection
import com.eladfinish.bible.contest.ui.menus.actions.CompleteVerseToolbarAction
import dev.icerock.moko.resources.StringResource
import dev.icerock.moko.resources.desc.StringDesc

@Immutable
data class CompleteVerseState(
    val isSkipReadMeChecked: Boolean,
    val completeMode: CompleteMode,
    val versePart: VersePart, // which part of verse to be shown and the other to be completed
    val isShakeEnabled: Boolean,
    val howManyWordsToHide: Int,
    val hebrewGender: HebrewGender,
    val verseWithBlanks: VerseWithBlanks,
    val inputHint: StringDesc,
    val isEditTextEnabled: Boolean,
    val inputTextColor: Int,
    val showArrows: Boolean,
    val showButtonNextVerse: Boolean,
    val isRevealButtonVisible: Boolean,
    val isFullVerseVisible: Boolean,
    val isVerseSourceVisible: Boolean, // verse location shown under the parchment once the verse is completed
    val dialogState: DialogState,
) : StateKt {

    @Stable
    sealed class DialogState {
        data class Help(
            val isDontShowAgainChecked: Boolean,
            val content: String,
        ) : DialogState()

        data class Error(
            val message: String,
        ) : DialogState()

        data class NumberPicker(
            val initialNumber: Int,
        ) : DialogState()

        data class ChapterDialog(
            val chapterData: ChapterData,
        ) : DialogState()

        data object None : DialogState()
    }
}

sealed class CompleteVerseAction : ActionKt {
    data object ShowNewVerse : CompleteVerseAction()
    data object KeyboardNextClicked : CompleteVerseAction()
    data object NumberPickerMenuItemClicked : CompleteVerseAction()
    data class HelpClicked(val input: String) : CompleteVerseAction()
    data class SetCompleteType(val completeMode: CompleteMode) : CompleteVerseAction()
    data class Config(val completeMode: CompleteMode, val versePart: VersePart) : CompleteVerseAction()
    data class UpdateHowManyWordsToHide(val value: Int) : CompleteVerseAction()
    data class HorizontalArrowClicked(val direction: HorizontalDirection, val clickType: ClickType) : CompleteVerseAction()
    data class CheckCorrectness(val input: String) : CompleteVerseAction()
    internal data class UpdateSpannableStringKMP(val verseWithBlanks: VerseWithBlanks) : CompleteVerseAction()
    data class ToolbarClicked(val toolbarAction: CompleteVerseToolbarAction) : CompleteVerseAction()
    data class ToggleHelp(val shouldShowNextTime: Boolean) : CompleteVerseAction()
    internal data class ErrorOccurred(val message: String) : CompleteVerseAction()
    data object HideDialog : CompleteVerseAction()
    data class SetFullVerseVisible(val visible: Boolean) : CompleteVerseAction()
    data object VerseSourceClicked : CompleteVerseAction()
}

sealed class CompleteVerseEffect : EffectKt {
    data class NotifyError(val text: String) : CompleteVerseEffect()
    data object ResetUI : CompleteVerseEffect()
    data object InputRequestFocus : CompleteVerseEffect()

    data class MoveCursor(val direction: HorizontalDirection) : CompleteVerseEffect()
    data class MoveCursorInfo(val direction: HorizontalDirection) : CompleteVerseEffect()
    data class OnCorrect(
        val allWordsCompleted: Boolean,
        val text: String,
        val completeMode: CompleteMode,
        val verseWithBlanks: VerseWithBlanks,
    ) : CompleteVerseEffect()

    data class ToastAndVibrate(
        val stringResource: StringResource,
        val duration: Long,
    ) : CompleteVerseEffect()

    data class ShowPasukWithBlanks(
        val animated: Boolean = true,
        val checkCorrectness: Boolean = false,
    ) : CompleteVerseEffect()
}
